import { isEqual } from 'lodash';
import { memo } from 'react';

import { type PublicClip } from '@air/api';

import { type GalleryViewRender } from '~/components/Gallery/types';
import { useGetPublicAssetMenuOptionsCallback } from '~/components/PublicBoard/useGetPublicAssetMenuOptionsCallback';
import { GalleryAsset } from '~/components/v2/components/shared/gallery/GalleryAsset';
import { usePublicGalleryAssetCardProps } from '~/components/v2/components/shared/gallery/usePublicGalleryAssetCardProps';
import { usePublicSelectionMenuOptions } from '~/hooks/usePublicSelectionMenuOptions';

export type PublicGalleryAssetProps = GalleryViewRender<PublicClip>;

export const PublicGalleryAsset = memo(
  ({ data: asset, box }: PublicGalleryAssetProps) => {
    // The card body (thumbnail, title, click targets, indicators) is the shared public contract —
    // the editor's canvas card renders the same body through this hook; only the menus below are
    // public-specific.
    const assetCardProps = usePublicGalleryAssetCardProps({ asset });
    const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
    const { getAssetMenuOptionsCallback } = useGetPublicAssetMenuOptionsCallback();
    const getItemMenuOptions = getAssetMenuOptionsCallback({ item: asset });

    return (
      <GalleryAsset
        height={box.height}
        width={box.width}
        clip={asset}
        isSelectable={true}
        key={asset.id}
        getItemMenuOptions={getItemMenuOptions}
        getSelectionMenuOptions={getSelectionMenuOptions}
        {...assetCardProps}
      />
    );
  },
  // `container` comes from `react-virtualized` and is a different object every time but may have the same values so we need to deep equal check the props
  isEqual,
);

PublicGalleryAsset.displayName = 'PublicGalleryAsset';
